import React from 'react';
import { useTranslation } from 'react-i18next';
import WithProcessError from 'hocs/WithProcessError';
import PropTypes from 'prop-types';

import Checkbox from 'components/Checkbox/Checkbox';
import FormItem from 'components/FormItem/FormItem';
import Input from 'components/Input/Input';
import Popup from 'components/Popup/Popup';

import Copy24 from 'icons/Copy24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import { paymentPeriods } from 'utils/constants';
import { validateNumber } from 'utils/validate';
import { useWildcard } from 'utils/wildcard';

import styles from './create-page-link.scss';

function CreateLinkPopup(props) {
    const { data, onClosed, onLinkCopied, processError, terminalType } = props;

    const auth = useAuth();
    const api = useApi();
    const { wildcard } = useWildcard();
    const { t } = useTranslation();

    const [defaultAmount, setDefaultAmount] = React.useState({
        value: data.default_regular_amount,
        error: null,
    });
    const [firstAmount, setFirstAmount] = React.useState({
        value: data.default_first_amount,
        error: null,
    });
    const [paymentsNumber, setPaymentsNumber] = React.useState({
        value: data.default_payments_number,
        error: null,
    });
    const [creditable, setCreditable] = React.useState({
        value: data.default_creditable,
        error: null,
    });

    const [link, setLink] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    function handleAmountChange(e) {
        const value = e.target.value;
        const error = validateNumber(value, 1, Infinity);
        setDefaultAmount({ value, error });
    }

    function handleFirstAmountChange(e) {
        const value = e.target.value;
        const error = validateNumber(value, 1, Infinity);
        setFirstAmount({ value, error });
    }

    function handlePaymentsNumberChange(e) {
        const value = e.target.value;
        const error = validateNumber(value, 1, 365);
        setPaymentsNumber({ value, error });
    }

    function handleCreditableChange(value) {
        setCreditable({ value, error: null });
    }

    function isYookassa() {
        return terminalType === 'Yookassa';
    }

    function isFormValid() {
        if (creditable.value) {
            if (defaultAmount.value < 3000) {
                setDefaultAmount((prev) => ({
                    value: prev.value,
                    error: new Error('Сумма рассрочки не может быть менее 3000 ₽'),
                }));

                return false;
            }
        }

        return true;
    }

    function handleConfirm() {
        if (defaultAmount.error !== null) return;

        window.ym(91769012, 'reachGoal', 'create_product_link');

        if (!isFormValid()) {
            return;
        }

        const companyId = auth?.company.id;
        const formId = data.id;
        const settings = {
            regular_amount: Number(defaultAmount.value),
            first_amount: data.is_recurrent ? Number(firstAmount.value) : Number(defaultAmount.value),
            payments_number: !data.is_unlimited ? Number(paymentsNumber.value) : undefined,
            creditable: Boolean(creditable.value),
        };

        if (companyId !== undefined && formId !== undefined) {
            setLoading(true);
            api.createFormCustomization(companyId, formId, settings)
                .then((data) => {
                    if (data.link) {
                        const protocol = wildcard === 'localhost' ? 'http' : 'https';
                        const link = `${protocol}://${window.location.host}/pay/${data.link}`;
                        setLink(link);
                    } else {
                        processError('Не удалось создать ссылку.');
                    }
                })
                .catch((error) => processError('Не удалось создать ссылку.', error))
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    function handleCopyLink() {
        if (link) {
            navigator.clipboard
                .writeText(link)
                .then(() => {
                    if (typeof onLinkCopied === 'function') {
                        onLinkCopied();
                    }
                })
                .catch((error) => processError('Не удалось скопировать ссылку.', error));
        }
    }

    const description = t('products.popup.createLink.description.' + (data.is_recurrent ? 'subscription' : 'payment'));
    const period = paymentPeriods[data.once_per_days] || 'период';
    const label = data.is_recurrent ? `Стоимость подписки (руб./${period})` : 'Сумма к оплате (руб.)';
    const getCancelText = () => {
        if (link === null) {
            return t('products.popup.createLink.cancelText.default');
        }

        return t('products.popup.createLink.cancelText.done');
    };

    return (
        <Popup
            canCloseOutside={true}
            maxWidth={380}
            title={t('products.popup.createLink.title')}
            description={description}
            onClosed={onClosed}
            loading={loading}
            confirmText={t('products.popup.createLink.confirm')}
            onConfirm={handleConfirm}
            noConfirm={link !== null}
            cancelText={getCancelText()}
        >
            <div className={styles.wrapper}>
                {!link ? (
                    <>
                        <FormItem label={label} required={true} error={defaultAmount.error}>
                            <Input
                                type="text"
                                name={'default_regular_amount'}
                                onChange={handleAmountChange}
                                value={defaultAmount.value}
                                placeholder="Сумма в рублях"
                            />
                        </FormItem>

                        {data.is_recurrent && (
                            <FormItem label={'Первый платёж (руб.)'} required={true} error={firstAmount.error}>
                                <Input
                                    type="text"
                                    name={'default_first_amount'}
                                    onChange={handleFirstAmountChange}
                                    value={firstAmount.value}
                                    placeholder="Сумма в рублях"
                                />
                            </FormItem>
                        )}

                        {!data.is_unlimited && data.is_recurrent && (
                            <FormItem
                                label={t('productConstructor.field.numberOfPayments.label')}
                                required={true}
                                error={paymentsNumber.error}
                            >
                                <Input
                                    type="text"
                                    name={'default_first_amount'}
                                    onChange={handlePaymentsNumberChange}
                                    value={paymentsNumber.value}
                                    placeholder={t('productConstructor.field.numberOfPayments.placeholder')}
                                />
                            </FormItem>
                        )}

                        {!data.is_recurrent && isYookassa() && (
                            <Checkbox defaultChecked={data.default_creditable} onCheck={handleCreditableChange}>
                                Предложить рассрочку
                            </Checkbox>
                        )}
                    </>
                ) : null}
                {link && (
                    <FormItem
                        label={t('products.popup.createLink.linkLabel')}
                        caption={t('products.popup.createLink.linkCaption')}
                    >
                        <div className={styles.linkBox}>
                            <a href={link} target="_blank" rel="nofollow noreferrer">
                                {link}
                            </a>
                            <div className={styles.copy} onClick={handleCopyLink}>
                                <Copy24 />
                            </div>
                        </div>
                    </FormItem>
                )}
            </div>
        </Popup>
    );
}

CreateLinkPopup.propTypes = {
    onClosed: PropTypes.func,
    onLinkCopied: PropTypes.func,
    terminalType: PropTypes.oneOf(['Tinkoff', 'Yookassa']),
    data: PropTypes.shape({
        id: PropTypes.number,
        is_recurrent: PropTypes.bool,
        link: PropTypes.string,
        default_regular_amount: PropTypes.number,
        default_first_amount: PropTypes.number,
        default_payments_number: PropTypes.number,
        default_creditable: PropTypes.bool,
        is_unlimited: PropTypes.bool,
        once_per_days: PropTypes.number,
    }),
    ...WithProcessError.propTypes,
};

export default WithProcessError(CreateLinkPopup);
